import React from 'react';
import { Button } from '@naf-simba/elements-buttons';
import { mapSimbaVariant, type LegacyButtonVariant } from '@utils/simba';
import { useDocumentUrlWithNode } from '../../hooks/Documents/useDocumentUrl';
import { useTrackCTA } from '../../utils/Tracking/CtaClick/TrackCtaClick';
import { useTrackPromotion } from '../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../utils/Tracking/Promotion/getPromotionEventTrackingData';
import * as S from './ModularButton.Styles';
import { ButtonType, ModularExternalLinkType } from '../../../../types/ModularButtonBlockType';

interface ModularButtonProps {
  primaryButton: ButtonType & { variant?: string };
  secondaryButton?: ButtonType;
}

export const ModularButton = ({ primaryButton, secondaryButton }: ModularButtonProps) => {
  const primaryInternalLink = useDocumentUrlWithNode(primaryButton?.link?.data);
  const secondaryInternalLink = useDocumentUrlWithNode(secondaryButton?.link?.data);
  const trackCTA = useTrackCTA();

  const primaryLinkData = primaryButton.link?.data;
  const secondaryLinkData = secondaryButton?.link?.data;

  const primaryButtonExternalLink = primaryLinkData
    ? ({
        ...primaryLinkData,
        blank: primaryLinkData.blank ?? false,
        key: (primaryLinkData as ModularExternalLinkType).href ?? '',
      } as ModularExternalLinkType)
    : null;
  const secondaryButtonExternalLink = secondaryLinkData
    ? ({
        ...secondaryLinkData,
        blank: secondaryLinkData.blank ?? false,
        key: (secondaryLinkData as ModularExternalLinkType).href ?? '',
      } as ModularExternalLinkType)
    : null;

  const primaryUrl = primaryInternalLink || primaryButtonExternalLink.href;
  const secondaryUrl = secondaryInternalLink || secondaryButtonExternalLink?.href;
  const primaryPromotion = getPromotionEventTrackingData(primaryButton.buttonText, primaryUrl);
  const secondaryPromotion = secondaryButton
    ? getPromotionEventTrackingData(secondaryButton?.buttonText, secondaryUrl)
    : undefined;
  const shouldTrackPrimaryPromotion = Boolean(primaryPromotion);
  const shouldTrackSecondaryPromotion = Boolean(secondaryPromotion);
  const { wrapperRef: primaryWrapperRef, trackSelectPromotion: trackPrimarySelect } = useTrackPromotion({
    creative_slot: 'modular-button',
    creative_name: `CTA | primary | ${primaryButton?.buttonText}`,
    promotion_id: primaryPromotion?.promotion_id,
    promotion_name: primaryPromotion?.promotion_name,
  });
  const { wrapperRef: secondaryWrapperRef, trackSelectPromotion: trackSecondarySelect } = useTrackPromotion({
    creative_slot: 'modular-button',
    creative_name: `CTA | outline | ${secondaryButton?.buttonText}`,
    promotion_id: secondaryPromotion?.promotion_id,
    promotion_name: secondaryPromotion?.promotion_name,
  });

  if (!primaryLinkData) return null;

  const handleTracking = (text: string, path: string) => {
    trackCTA({
      cta_position: 'content',
      text,
      url: path,
    });
  };

  const renderButton = (
    text: string,
    variant: string,
    internalLink?: string,
    externalLink?: ModularExternalLinkType,
    promotionRef?: React.RefObject<HTMLDivElement>,
    shouldTrackPromotion?: boolean,
    trackSelectPromotion?: () => void,
  ) => {
    if (!text) return null;
    const buttonVariantProps = mapSimbaVariant(variant as LegacyButtonVariant);
    const promotionWrapperRef = shouldTrackPromotion ? promotionRef : undefined;

    if (internalLink) {
      return (
        <div ref={promotionWrapperRef}>
          <Button
            variant={buttonVariantProps.variant}
            color={buttonVariantProps.color}
            shade={buttonVariantProps.shade}
            to={internalLink}
            onClick={() => {
              if (shouldTrackPromotion && trackSelectPromotion) {
                trackSelectPromotion();
              }
              handleTracking(text, internalLink);
            }}
          >
            {text}
          </Button>
        </div>
      );
    }

    if (externalLink) {
      return (
        <div ref={promotionWrapperRef}>
          <Button
            variant={buttonVariantProps.variant}
            color={buttonVariantProps.color}
            shade={buttonVariantProps.shade}
            href={externalLink.href}
            target={externalLink.blank ? '_blank' : '_self'}
            onClick={() => {
              if (shouldTrackPromotion && trackSelectPromotion) {
                trackSelectPromotion();
              }
              handleTracking(text, externalLink.href);
            }}
          >
            {text}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <S.ButtonContainer>
      {renderButton(
        primaryButton.buttonText,
        primaryButton.variant || 'signature',
        primaryInternalLink,
        primaryButtonExternalLink,
        primaryWrapperRef,
        shouldTrackPrimaryPromotion,
        trackPrimarySelect,
      )}
      {secondaryButton &&
        secondaryButton.buttonText &&
        renderButton(
          secondaryButton.buttonText,
          'outline',
          secondaryInternalLink,
          secondaryButtonExternalLink || undefined,
          secondaryWrapperRef,
          shouldTrackSecondaryPromotion,
          trackSecondarySelect,
        )}
    </S.ButtonContainer>
  );
};
